import { Switch } from '@headlessui/react';
import { CheckIcon, XIcon } from '@phosphor-icons/react';
import { useField, useFormikContext } from 'formik';

import { Text } from '@components';
import { InputMod } from '@enums';
import { cn } from '@utils';

interface ToggleButtonProps {
  name?: string;
  className?: string;
  disabled?: boolean;
  label?: string;
  inputMod?: InputMod;
  description?: string;
  checked?: boolean;
  onChange?: (checked: boolean) => void;
}

export const ToggleButton = ({
  name,
  disabled,
  className = '',
  label,
  inputMod = InputMod.Default,
  description,
  checked,
  onChange,
}: ToggleButtonProps) => {
  const isFormikMode = Boolean(name);
  const [field] = useField(name ?? '');
  const { setFieldValue } = useFormikContext<{ [key: string]: boolean }>();

  const isFilled = inputMod === InputMod.Filled;
  const isChecked = isFormikMode ? field.value : checked;

  const handleToggle = (newValue: boolean) => {
    if (isFormikMode && name) {
      setFieldValue(name, newValue);
    } else if (onChange) {
      onChange(newValue);
    }
  };

  const switchContainerClasses = cn(
    label && isFilled && 'flex flex-col w-full ring-1 ring-theme-border-default rounded-md pt-1.5 pb-3 px-3 space-y-1',
    className,
  );

  const switchClasses = cn(
    'relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-0',
    isChecked && 'bg-theme-primary-main',
    !isChecked && 'bg-gray-200',
    disabled && 'hover:cursor-not-allowed bg-opacity-50',
    !disabled && 'hover:cursor-pointer',
  );

  const iconClasses = (current: boolean) => {
    return cn(
      'absolute inset-0 flex h-full w-full items-center justify-center transition-opacity',
      current ? 'duration-100 ease-out' : 'duration-200 ease-in',
      ((!disabled && !current) || (disabled && current)) && 'text-gray-400',
      !disabled && current && 'text-theme-primary-main',
      disabled && !current && 'text-gray-300',
    );
  };

  const containerButtonClasses = cn(
    'pointer-events-none relative inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
    isChecked ? 'translate-x-5' : 'translate-x-0',
    disabled && 'bg-opacity-70',
  );

  return (
    <div className={switchContainerClasses}>
      {isFilled && label && (
        <Text $level={5} $customizeColor className="text-gray-400">
          {label}
        </Text>
      )}
      <div className={cn('flex flex-1', isFilled && label && description ? 'pb-2' : '')}>
        {isFilled && label && (
          <Text
            className={`${
              isChecked ? 'ring-green-400 ring-bg-50 text-green-400' : 'ring-red-400 bg-red-50 text-red-400'
            } ring-1 rounded-md py-1.5 px-2.5 text-us font-normal`}
          >
            {isChecked ? 'Enabled' : 'Disabled'}
          </Text>
        )}

        <Switch disabled={disabled} checked={isChecked ?? false} onChange={handleToggle} className={switchClasses}>
          <span className={containerButtonClasses}>
            <span className={iconClasses(isChecked)}>
              {isChecked ? <CheckIcon weight="bold" className="size-3" /> : <XIcon weight="bold" className="size-3" />}
            </span>
          </span>
        </Switch>
      </div>
      {description && isFilled && (
        <Text
          $level={6}
          $customizeColor
          className=" ring-1 py-1.5 px-2.5 bg-red-50 ring-red-300 text-red-400 rounded-md"
        >
          {description}
        </Text>
      )}
    </div>
  );
};
